import React from "react";
import { RECOMMENDATIONS_BY_STEP } from "../../data/tours/sanJuanTourData";
import { toMapsUrl } from "../../utils/tourUtils";
import { colors, radius, spacing, elevation } from "../../utils/designSystem";

// Localized strings
const STRINGS = {
  en: {
    ariaLabel: "Nearby Recommendations",
    emptyMessage: "Head to the next location. We have more recommendations coming up.",
    defaultName: "Recommended spot",
    openInMaps: "Open in Maps",
    copy: "Copy",
  },
  es: {
    ariaLabel: "Lugares Cercanos",
    emptyMessage: "Continúa al siguiente lugar. Tenemos más recomendaciones adelante.",
    defaultName: "Lugar recomendado",
    openInMaps: "Abrir en Mapas",
    copy: "Copiar",
  }
};

export function LocalRecommendations({ step, limit = 2, lang = "en" }) {
  const t = STRINGS[lang] || STRINGS.en;
  const data =
    (step && step.id && RECOMMENDATIONS_BY_STEP[step.id]) || [];

  const limitedData = data.slice(0, limit);

  if (limitedData.length === 0) {
    return (
      <section
        aria-label={t.ariaLabel}
        className="mt-4 text-center text-sm italic"
        style={{
          background: colors.neutral[50],
          border: `1px solid ${colors.neutral[200]}`,
          borderRadius: radius.lg,
          padding: spacing[4],
          color: colors.neutral[500],
        }}
      >
        {t.emptyMessage}
      </section>
    );
  }

  return (
    <section aria-label={t.ariaLabel} className="mt-4 space-y-3">
      {limitedData.map((rec, index) => {
        const photo = rec.photo || null;
        const resolvedMapsUrl =
          rec.mapsUrl || toMapsUrl(rec.name, rec.address);
        // Use Spanish blurb if available and lang is Spanish
        const displayBlurb = lang === "es" && rec.blurbEs ? rec.blurbEs : rec.blurb;
        // Use Spanish category if available
        const displayCategory = lang === "es" && rec.categoryEs ? rec.categoryEs : rec.category;
        return (
          <article
            key={rec.id || rec.name || index}
            className="overflow-hidden transition-all duration-300"
            style={{
              background: colors.neutral.white,
              border: `1px solid ${colors.neutral[200]}`,
              borderRadius: radius.xl,
              boxShadow: elevation.sm,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = elevation.md;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = elevation.sm;
            }}
          >
            {/* Image with category badge overlay */}
            <div className="relative">
              {photo ? (
                <img
                  src={photo}
                  alt={rec.name || "Nearby recommendation"}
                  loading="lazy"
                  className="w-full h-40 object-cover"
                />
              ) : (
                <div
                  className="w-full h-40 flex items-center justify-center text-4xl"
                  style={{
                    background: colors.neutral[100],
                    color: colors.neutral[400],
                  }}
                >
                  📍
                </div>
              )}

              {/* Category badge */}
              {displayCategory && (
                <div
                  className="absolute top-3 left-3 text-xs font-semibold px-3 py-1"
                  style={{
                    background: colors.primary.main,
                    color: colors.neutral.white,
                    borderRadius: radius.full,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  {displayCategory}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex flex-col" style={{ padding: spacing[4] }}>
              <div>
                <h3
                  className="text-base font-bold leading-tight"
                  style={{ color: colors.neutral[900] }}
                >
                  {rec.name || t.defaultName}
                </h3>
                {displayBlurb && (
                  <p
                    className="mt-2 text-sm leading-relaxed"
                    style={{ color: colors.neutral[700] }}
                  >
                    {displayBlurb}
                  </p>
                )}
                {rec.address && (
                  <p
                    className="mt-2 text-xs"
                    style={{ color: colors.neutral[500] }}
                  >
                    {rec.address}
                  </p>
                )}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {(rec.name || rec.address) && (
                  <a
                    href={resolvedMapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold transition-all"
                    style={{
                      background: colors.neutral[900],
                      color: colors.neutral.white,
                      borderRadius: radius.full,
                      padding: `${spacing[2]} ${spacing[3]}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = colors.neutral[700];
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = colors.neutral[900];
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {t.openInMaps}
                  </a>
                )}

                {rec.address && (
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${rec.name || ""}\n${rec.address || ""}`.trim()
                      );
                    }}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold transition-all"
                    style={{
                      background: colors.neutral[100],
                      color: colors.neutral[700],
                      border: `1px solid ${colors.neutral[200]}`,
                      borderRadius: radius.full,
                      padding: `${spacing[2]} ${spacing[3]}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = colors.neutral[200];
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = colors.neutral[100];
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    {t.copy}
                  </button>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}
